/**
 * The same-origin fetch backstop (M4S-5670, docs/pdlc/cs-view-as-client.md §6).
 *
 * A `window.fetch` wrapper, installed once when this module loads in the
 * browser (the provider imports it for that side effect) and never removed. It
 * is a no-op while the tab holds no View-as session, so a tab that is not
 * viewing as a client pays one store read per request and nothing else.
 *
 * While the tab holds a session it checks only same-origin requests to
 * `/api/*` whose method is not GET or HEAD, as `client: 'next'`, through the
 * shared guard (`backstop-shared.ts`). It passes untouched:
 *
 * - cross-origin calls, the DANI stream included (the send guard covers it);
 * - `/api/view-as/*`, so recording a write never recurses into the backstop;
 * - `/api/auth/*` (NextAuth), so the staff member can always sign out.
 *
 * An allowed write that comes back 2xx is recorded, then each after-response
 * hook runs before the response is handed back. The one hook today is the D21
 * conversion hand-over, `handOverConvertedCrosstab`.
 *
 * Bodies: only a string body (JSON) is read for the policy. A stream, a
 * FormData or a Request's own body is never read, so nothing is consumed.
 */

import { toast } from 'react-toastify';

import { endViewAs } from '@/@subsidiary/view-as-lifecycle';
import { getViewAsSnapshot } from '@/@subsidiary/view-as-store';
import { guardViewAsWrite, recordViewAsWrite } from '@/lib/view-as/backstop-shared';
import { isViewAsSessionCode } from '@/lib/view-as/client';
import { normaliseViewAsPath, parseViewAsBody } from '@/lib/view-as/write-policy';

type FetchFn = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** Marks `window` once the wrapper is installed, so a second load (HMR, a second bundle) never wraps it twice. */
const INSTALLED = Symbol.for('mfour.viewAs.fetchBackstop');

/**
 * Routes passed untouched in any status: View-as's own (so recording never
 * recurses), and NextAuth's (sign-out and the staff member's own session must
 * work even while a session is restoring or ending).
 */
const UNTOUCHED_ROUTES = ['/api/view-as/', '/api/auth/'] as const;
const HANDOVER_URL = '/api/view-as/crosstab-handover';
const DANI_LAYOUT_PATH = /^\/api\/crosstab\/dani-layout\/project\/[^/]+$/;

/** The notice when a conversion could not be handed to the client (D21). */
export const CONVERSION_KEPT_AS_DRAFT = 'Converted, but kept as your draft.';

/** What an after-response hook is told about a write that came back 2xx. */
export interface AfterResponseContext {
    method: string;
    path: string;
    /** The JSON body, when the request sent one as a string. */
    body: Record<string, unknown> | null;
    /** Whether the request body was a string that parsed as a JSON object. */
    bodyRead: boolean;
    response: Response;
    /** The unwrapped fetch, for a hook's own calls. */
    fetch: FetchFn;
}

export type AfterResponseHook = (context: AfterResponseContext) => Promise<void>;

function requestUrl(input: RequestInfo | URL): string | null {
    if (typeof input === 'string') return input;
    if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
    const url = (input as { url?: unknown } | null)?.url;
    return typeof url === 'string' ? url : null;
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
    const method =
        init?.method ??
        (typeof input === 'object' && input && 'method' in input
            ? (input as { method?: string }).method
            : undefined);
    return String(method || 'GET').toUpperCase();
}

/** The normalised `/api/...` path of a same-origin request, else null. */
function sameOriginApiPath(url: string): string | null {
    let resolved: URL;
    try {
        resolved = new URL(url, window.location.href);
    } catch {
        return null;
    }
    if (resolved.origin !== window.location.origin) return null;
    const path = normaliseViewAsPath(resolved.pathname);
    return path.startsWith('/api/') ? path : null;
}

/**
 * D21: a classic crosstab converted while viewing as a client belongs to the
 * client. After a 2xx keyless dani-layout POST (the create) on a page whose
 * URL carries `convert=<sourceKey>`, hand the new record over through
 * `POST /api/view-as/crosstab-handover`, and only then let the workspace see
 * the response. A `view_as_*` refusal ends View-as; any other failure leaves
 * the crosstab as the staff member's draft and says so. Never throws.
 */
export async function handOverConvertedCrosstab(context: AfterResponseContext): Promise<void> {
    try {
        if (context.method !== 'POST' || !DANI_LAYOUT_PATH.test(context.path)) return;
        if (!context.bodyRead || !context.body) return;
        const key = context.body.key;
        if (typeof key === 'string' && key.trim()) return;

        const sourceKey = new URLSearchParams(window.location.search).get('convert')?.trim();
        if (!sourceKey) return;
        const sessionId = getViewAsSnapshot().sessionId;
        if (!sessionId) return;

        let layoutKey: string | null = null;
        try {
            const data = (await context.response.clone().json()) as { key?: unknown } | null;
            layoutKey = typeof data?.key === 'string' && data.key.trim() ? data.key.trim() : null;
        } catch {
            layoutKey = null;
        }
        if (!layoutKey) {
            toast.warning(CONVERSION_KEPT_AS_DRAFT);
            return;
        }

        let res: Response;
        try {
            res = await context.fetch(HANDOVER_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId, layoutKey, sourceKey }),
                credentials: 'same-origin',
                cache: 'no-store',
            });
        } catch {
            toast.warning(CONVERSION_KEPT_AS_DRAFT);
            return;
        }
        if (res.ok) return;

        let error: { error?: unknown; message?: unknown } | null = null;
        try {
            error = (await res.json()) as { error?: unknown; message?: unknown } | null;
        } catch {
            error = null;
        }
        if (typeof error?.error === 'string' && isViewAsSessionCode(error.error)) {
            endViewAs(
                typeof error.message === 'string' && error.message
                    ? error.message
                    : 'Studio could not confirm the View as session.',
            );
            return;
        }
        toast.warning(CONVERSION_KEPT_AS_DRAFT);
    } catch {
        // An after-response hook never fails the write it follows.
    }
}

/** Run after an allowed write's 2xx, in order, before the response is handed back. */
export const AFTER_RESPONSE_HOOKS: readonly AfterResponseHook[] = [handOverConvertedCrosstab];

/** The wrapper around the original fetch. Exported for tests; the app uses the installed one. */
export function createViewAsFetch(original: FetchFn): FetchFn {
    return async function viewAsFetch(input: RequestInfo | URL, init?: RequestInit) {
        if (!getViewAsSnapshot().sessionId) return original(input, init);

        const url = requestUrl(input);
        const path = url ? sameOriginApiPath(url) : null;
        if (!path || UNTOUCHED_ROUTES.some((route) => path.startsWith(route))) {
            return original(input, init);
        }

        const method = requestMethod(input, init);
        if (method === 'GET' || method === 'HEAD') return original(input, init);

        const rawBody = typeof init?.body === 'string' ? init.body : undefined;
        const body = rawBody === undefined ? null : parseViewAsBody(rawBody);

        const guarded = await guardViewAsWrite({
            client: 'next',
            method,
            path,
            body: body ?? undefined,
        });
        const response = await original(input, init);
        if (response.ok && guarded) {
            recordViewAsWrite(guarded, response.status);
            for (const hook of AFTER_RESPONSE_HOOKS) {
                await hook({
                    method,
                    path,
                    body,
                    bodyRead: body != null,
                    response,
                    fetch: original,
                });
            }
        }
        return response;
    };
}

/**
 * Install the wrapper on `window.fetch`. Idempotent, and a no-op on the server
 * or where there is no fetch. Returns whether the wrapper is in place.
 */
export function installViewAsFetchBackstop(): boolean {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return false;
    const marked = window as unknown as Record<symbol, unknown>;
    if (marked[INSTALLED]) return true;
    window.fetch = createViewAsFetch(window.fetch.bind(window));
    marked[INSTALLED] = true;
    return true;
}

installViewAsFetchBackstop();
